import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { navigationTranslations } from '../translations/navigation';

const NavbarContainer = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2.7rem 2rem;
  background-color: white;
  box-shadow: none;
  font-family: 'Montserrat', sans-serif;
  position: relative;
  
  @media (max-width: 992px) {
    justify-content: space-between;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2.5rem;
  justify-content: center;
  position: relative;
  
  @media (max-width: 992px) {
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    padding: 6rem 2rem 2rem;
    box-shadow: none;
    align-items: center;
    justify-content: flex-start;
    text-align: center;
    z-index: 999;
    gap: 2rem;
  }
`;

const MovingUnderline = styled.div`
  position: absolute;
  bottom: -5px;
  height: 2px;
  background-color: #000000;
  transition: left 0.3s ease, width 0.3s ease;
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${({ active }) => (active ? '#000000' : '#555555')};
  text-decoration: none;
  font-weight: 600;
  font-size: 0.675rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  transition: color 0.3s ease;
  position: relative;
  cursor: pointer;
  
  &:hover {
    color: #000000;
  }
  
  @media (max-width: 992px) {
    text-align: center;
    width: 100%;
    font-size: 1.2rem;
    padding: 1rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  
  @media (max-width: 992px) {
    display: block;
    padding: 0.5rem;
  }
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const navLinksRef = useRef(null);
  const location = useLocation();
  const { language } = useLanguage();
  
  // Update underline position when route or language changes
  useEffect(() => {
    const updateUnderlinePosition = () => {
      if (!navLinksRef.current) return;
      
      const navItems = Array.from(navLinksRef.current.children);
      const activeNavItem = navItems.find(item => {
        const link = item.querySelector('a');
        if (!link) return false;
        const href = link.getAttribute('href');
        return href === location.pathname || (href === '/' && location.pathname === '/');
      });
      
      if (activeNavItem) {
        const containerRect = navLinksRef.current.getBoundingClientRect();
        const itemRect = activeNavItem.getBoundingClientRect();
        
        setUnderlineStyle({
          left: itemRect.left - containerRect.left,
          width: itemRect.width
        });
      }
    };

    // Update position immediately and after a short delay to handle font loading
    updateUnderlinePosition();
    setTimeout(updateUnderlinePosition, 100);
    window.addEventListener('resize', updateUnderlinePosition);
    
    return () => window.removeEventListener('resize', updateUnderlinePosition);
  }, [location.pathname, language]);
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  const closeMenu = () => {
    setIsOpen(false);
  };
  
  const t = navigationTranslations[language];
  
  return (
    <NavbarContainer>
      <MobileMenuButton onClick={toggleMenu}>
        {isOpen ? '✕' : '☰'}
      </MobileMenuButton>
      
      <NavLinks isOpen={isOpen} ref={navLinksRef}>
        <NavLink 
          to="/"
          active={location.pathname === '/' ? 1 : 0}
          onClick={closeMenu}
        >
          {t.home}
        </NavLink>
        <NavLink 
          to="/about"
          active={location.pathname === '/about' ? 1 : 0}
          onClick={closeMenu}
        >
          {t.about}
        </NavLink>
        <NavLink 
          to="/consulting"
          active={location.pathname === '/consulting' ? 1 : 0}
          onClick={closeMenu}
        >
          {t.consulting}
        </NavLink>
        <NavLink 
          to="/products"
          active={location.pathname === '/products' ? 1 : 0}
          onClick={closeMenu}
        >
          {t.products}
        </NavLink>
        <NavLink 
          to="/partnerships"
          active={location.pathname === '/partnerships' ? 1 : 0}
          onClick={closeMenu}
        >
          {t.community}
        </NavLink>
        
        <MovingUnderline 
          style={{ 
            left: underlineStyle.left, 
            width: underlineStyle.width 
          }} 
        />
      </NavLinks>
      
      <LanguageSwitcher />
    </NavbarContainer>
  );
};

export default Navbar; 